import React, { useEffect, useState, useCallback, useRef } from 'react';
import { verifyRecaptchaV3 } from './ReCaptchaAxiosFuncs';
import { loadRecaptchaScriptOnce, canAttemptVerification, startVerification, endVerification } from './loadRecaptchaScriptOnce';
import { config } from '../../Utils/config';

export const ReCaptcha = ({ 
  onVerificationComplete, 
  onVerificationError,
  action = 'landing_page',
  userId = null,
  children,
  className = '',
  disabled = false,
  autoExecute = false
}) => {
  const [recaptchaReady, setRecaptchaReady] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState('');
  const [hasExecuted, setHasExecuted] = useState(false);
  const isVerifyingRef = useRef(false);

  const executeRecaptcha = useCallback(async () => {
    if (!recaptchaReady || !window.grecaptcha || isVerifying || disabled || hasExecuted || isVerifyingRef.current) {
      return;
    }

    // Check global verification tracking
    if (!canAttemptVerification(action)) {
      //console.log(`[${action}] Maximum recaptcha verification attempts reached.`);
      return;
    }

    setIsVerifying(true);
    isVerifyingRef.current = true;
    startVerification(action); // Start global tracking
    setError('');
    setHasExecuted(true); // Prevent re-execution

    try {
      // Google token fetch timing
      const tokenFetchStartTime = performance.now();
      const token = await window.grecaptcha.execute(config.recaptchaV3DevSiteKey, { action });
      const tokenFetchEndTime = performance.now();
      const tokenFetchDuration = (tokenFetchEndTime - tokenFetchStartTime) / 1000; // Convert to seconds
      
      //console.log(`[${action}] Acquired reCaptcha token in ${tokenFetchDuration.toFixed(3)}s`);
      
      const body = {
        token: token,
        captchaAction: action,
      };

      if (userId) {
        body.userId = userId;
      }

      // Backend verification timing
      const backendVerifyStartTime = performance.now();
      const result = await verifyRecaptchaV3(token, action, userId);
      const backendVerifyEndTime = performance.now();
      const backendVerifyDuration = (backendVerifyEndTime - backendVerifyStartTime) / 1000; // Convert to seconds
      
      //console.log(`[${action}] Verified with reCaptcha server call in ${backendVerifyDuration.toFixed(3)}s`);
      
      if (result.status === 'success' || result.data) {
        onVerificationComplete?.(result.data);
      } else {
        throw new Error('Verification failed');
      }
    } catch (e) {
      const errorMsg = `reCAPTCHA verification failed: ${e.message || 'Unknown error'}`;
      setError(errorMsg);
      onVerificationError?.(errorMsg);
      // Reset hasExecuted on error so it can retry
      setHasExecuted(false);
    } finally {
      setIsVerifying(false);
      isVerifyingRef.current = false;
      endVerification(action); // End global tracking
    }
  }, [recaptchaReady, isVerifying, disabled, hasExecuted, action, userId]);

  useEffect(() => {
    const loadRecaptcha = async () => {
      try {
        if (!config.recaptchaV3DevSiteKey) {
          throw new Error("Missing reCAPTCHA site key");
        }

        await loadRecaptchaScriptOnce(`https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(config.recaptchaV3DevSiteKey)}`);
        
        if (!window.grecaptcha) {
          throw new Error("grecaptcha not available");
        }

        window.grecaptcha.ready(() => {
          setRecaptchaReady(true);
          
          // Add hidden label for reCAPTCHA response textarea
          setTimeout(() => {
            const recaptchaResponse = document.querySelector('.g-recaptcha-response');
            if (recaptchaResponse) {
              // Get the actual ID of the element
              const elementId = recaptchaResponse.id;
              
              // Create hidden label
              const label = document.createElement('label');
              label.setAttribute('for', elementId);
              label.textContent = 'reCAPTCHA response';
              label.style.display = 'none';
              
              // Insert label before the textarea
              recaptchaResponse.parentNode.insertBefore(label, recaptchaResponse);
              
              //console.log('Added hidden label for reCAPTCHA response textarea with ID:', elementId);
            }
          }, 100);
        });
      } catch (e) {
        const errorMsg = `Failed to load reCAPTCHA: ${e.message || 'Script loading failed'}`;
        setError(errorMsg);
        onVerificationError?.(errorMsg);
      }
    };

    loadRecaptcha();
  }, []);

  // Reset hasExecuted when action changes (not autoExecute)
  useEffect(() => {
    setHasExecuted(false);
  }, [action]);

  // Auto-execute when ready if autoExecute is true
  useEffect(() => {
    if (autoExecute && recaptchaReady && !isVerifying && !hasExecuted) {
      executeRecaptcha();
    }
  }, [autoExecute, recaptchaReady, isVerifying, hasExecuted]);

  const handleClick = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    executeRecaptcha();
  }, [executeRecaptcha]);

  if (children) {
    return (
      <div className={className} onClick={handleClick}>
        {React.cloneElement(children, {
          disabled: disabled || isVerifying || !recaptchaReady,
          onClick: handleClick
        })}
        {error && (
          <div className="text-danger small mt-2">
            {error}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={className}>
      <button
        type="button"
        className="btn btn-primary"
        onClick={handleClick}
        disabled={disabled || isVerifying || !recaptchaReady}
      >
        {isVerifying ? 'Verifying...' : 'Verify'}
      </button>
      {error && (
        <div className="text-danger small mt-2">
          {error}
        </div>
      )}
    </div>
  );
};

export default ReCaptcha;